<template>
  <section class="settings-card leave-defaults-card">
    <div class="settings-card-header">
      <div class="settings-card-icon leave-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 21s7-3.5 7-10V5l-7-2-7 2v6c0 6.5 7 10 7 10Z" />
          <path d="M9 12h6M12 9v6" />
        </svg>
      </div>
      <div class="settings-card-heading">
        <h3>Leave Default Hours</h3>
        <p>
          Default hours applied when a supervisor marks a day as leave. The
          reviewer can still override the hours for any individual day.
        </p>
      </div>
    </div>

    <div class="settings-form">
      <b-row class="settings-fields leave-fields">
        <b-col cols="12" sm="6" lg="3">
          <b-form-group label="Public Holiday" label-class="form-label-sm">
            <div class="hours-input">
              <b-form-input
                v-model.number="form.defaultPublicHolidayHours"
                type="number"
                min="0"
                max="24"
                step="0.1"
                placeholder="0"
              />
              <span>hrs</span>
            </div>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6" lg="3">
          <b-form-group label="Personal Leave" label-class="form-label-sm">
            <div class="hours-input">
              <b-form-input
                v-model.number="form.defaultPersonalLeaveHours"
                type="number"
                min="0"
                max="24"
                step="0.1"
                placeholder="0"
              />
              <span>hrs</span>
            </div>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6" lg="3">
          <b-form-group label="Sick Leave" label-class="form-label-sm">
            <div class="hours-input">
              <b-form-input
                v-model.number="form.defaultSickLeaveHours"
                type="number"
                min="0"
                max="24"
                step="0.1"
                placeholder="0"
              />
              <span>hrs</span>
            </div>
          </b-form-group>
        </b-col>
        <b-col cols="12" sm="6" lg="3">
          <b-form-group label="Annual Leave" label-class="form-label-sm">
            <div class="hours-input">
              <b-form-input
                v-model.number="form.defaultAnnualLeaveHours"
                type="number"
                min="0"
                max="24"
                step="0.1"
                placeholder="0"
              />
              <span>hrs</span>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
      <div class="settings-footer leave-footer">
        <p class="leave-hint">
          Use 0–24 hours. Decimals such as 7.6 are supported.
        </p>
        <b-button
          type="button"
          variant="primary"
          class="save-button"
          :disabled="saving"
          @click="save"
        >
          <span v-if="saving" class="button-spinner" aria-hidden="true" />
          {{ saving ? 'Saving…' : 'Save Leave Defaults' }}
        </b-button>
      </div>
    </div>
  </section>
</template>

<script>
function numOrNull(v) {
  return v === null || v === undefined ? null : v
}

export default {
  name: 'LeaveDefaultsSettingsCard',
  data() {
    return {
      saving: false,
      form: {
        defaultPublicHolidayHours: null,
        defaultPersonalLeaveHours: null,
        defaultSickLeaveHours: null,
        defaultAnnualLeaveHours: null,
      },
    }
  },
  computed: {
    settings() {
      return this.$store.getters['timesheet/timesheetSettings']
    },
  },
  watch: {
    settings: {
      immediate: true,
      handler(s) {
        if (!s) return
        this.form.defaultPublicHolidayHours = numOrNull(
          s.default_public_holiday_hours,
        )
        this.form.defaultPersonalLeaveHours = numOrNull(
          s.default_personal_leave_hours,
        )
        this.form.defaultSickLeaveHours = numOrNull(s.default_sick_leave_hours)
        this.form.defaultAnnualLeaveHours = numOrNull(
          s.default_annual_leave_hours,
        )
      },
    },
  },
  methods: {
    async save() {
      this.saving = true
      try {
        await this.$store.dispatch('timesheet/updateTimesheetSettings', {
          default_public_holiday_hours: this.form.defaultPublicHolidayHours,
          default_personal_leave_hours: this.form.defaultPersonalLeaveHours,
          default_sick_leave_hours: this.form.defaultSickLeaveHours,
          default_annual_leave_hours: this.form.defaultAnnualLeaveHours,
        })
        this.$bvToast.toast('Leave default hours saved.', {
          title: 'Success',
          variant: 'success',
          solid: true,
        })
      } catch (e) {
        this.$bvToast.toast('Failed to save leave default hours.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.leave-defaults-card {
  margin-top: 20px;
}

.settings-card {
  background: #fff;
  border: 1px solid #e8edf3;
  border-radius: 14px;
  overflow: visible;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03),
    0 8px 24px rgba(15, 23, 42, 0.04);
}

.settings-card-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #eef2f6;
  background: #fff;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;

  .settings-card-icon {
    display: flex;
    flex: 0 0 42px;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    color: #087ff5;
    background: #eef7ff;
    border-radius: 11px;

    &.leave-icon {
      color: #0f9f6e;
      background: #ecfdf5;
    }

    svg {
      width: 21px;
      height: 21px;
      stroke: currentColor;
      stroke-width: 1.8;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  .settings-card-heading {
    min-width: 0;
  }

  h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.3;
    color: #172033;
  }

  p {
    margin: 5px 0 0;
    max-width: 610px;
    font-size: 13px;
    line-height: 1.5;
    color: #667085;
  }
}

.settings-form {
  padding: 22px 24px 24px;
  background: #fbfcfe;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
}

.settings-fields {
  margin-right: -8px;
  margin-left: -8px;

  > div {
    padding-right: 8px;
    padding-left: 8px;
  }
}

.settings-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding-top: 6px;
}

.save-button {
  min-width: 148px;
  min-height: 40px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 700;
  border: 0;
  border-radius: 9px;
  box-shadow: 0 4px 10px rgba(0, 123, 255, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:not(:disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(0, 123, 255, 0.24);
  }

  &:disabled {
    box-shadow: none;
  }
}

.button-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  margin-right: 6px;
  vertical-align: -2px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: settings-spin 0.7s linear infinite;
}

.hours-input {
  position: relative;

  span {
    position: absolute;
    top: 50%;
    right: 13px;
    z-index: 2;
    font-size: 12px;
    font-weight: 600;
    color: #98a2b3;
    pointer-events: none;
    transform: translateY(-50%);
  }

  /deep/ input {
    padding-right: 42px;
  }
}

.leave-footer {
  border-top: 1px solid #edf1f5;
  margin-top: 4px;
  padding-top: 18px;
}

.leave-hint {
  margin: 0;
  font-size: 12px;
  color: #7b8798;
}

@keyframes settings-spin {
  to {
    transform: rotate(360deg);
  }
}

/deep/ .form-label-sm {
  margin-bottom: 7px;
  font-size: 12px;
  font-weight: 700;
  color: #475467;
  text-transform: none;
  letter-spacing: 0;
}

/deep/ .settings-form {
  .form-group {
    margin-bottom: 18px;
  }

  .form-control {
    height: 44px;
    font-size: 14px;
    color: #243047;
    background-color: #fff;
    border-color: #d7dee8;
    border-radius: 9px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.02);

    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
    }
  }
}

@media (max-width: 575.98px) {
  .settings-card-header {
    align-items: flex-start;
    padding: 18px;
  }

  .settings-form {
    padding: 18px;
  }

  .settings-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .leave-hint {
    line-height: 1.5;
  }
}
</style>
